import * as React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { memo, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import Tabs, { TabElement } from '../../../Tabs/Tabs';

import Warnings from '../../../Warnings';
import Link from '../../../Link';
import { useConfig } from '../../../context';
import OrderCard from '../../../OrderCard';
import WxPagination from '../../../WxPagination';
import { usePagination } from '../../../WxPagination/utils';
import { useTheme } from '../../../theme';
import ProfileControls from '../../../ProfileControls';
import {
	OrderStatus,
	SortOrder,
	SortAttribute,
	useGetOrdersListPaginatedQuery,
	OrderFlownStatus
} from '@websky/graphql';

import SimpleLoader from '../../../SimpleLoader';
import { TABLET_MIN_WIDTH, redirectToOrder } from '../../../utils';
import EmptyPage from '../EmptyPage/EmptyPage';
import { useRenderers } from '../../../renderProps';

type BookingTabType = TabElement & {
	orderFlownStatus: OrderFlownStatus;
};

const ORDERS_PER_PAGE = 4;

const Orders: React.FC = memo(() => {
	const { Account: css } = useTheme('AccountModule');
	const { t } = useTranslation('Account');
	const {
		Account: { engineURL },
		global: { companyInfo }
	} = useConfig();

	const isTablet = useMediaQuery({ maxWidth: TABLET_MIN_WIDTH });

	const tabs: BookingTabType[] = [
		{
			header: t('Next fligths'),
			value: 0,
			orderFlownStatus: OrderFlownStatus.Active
		},
		{
			header: t('Archive'),
			value: 1,
			orderFlownStatus: OrderFlownStatus.Flown
		}
	];

	const [totalOrdersCount, setTotalOrdersCount] = useState(ORDERS_PER_PAGE);
	const [activeTab, setActiveTab] = React.useState(tabs[0]);

	const { pagesCount, paginationAvailable, handleChangePage, page } = usePagination(
		totalOrdersCount,
		ORDERS_PER_PAGE
	);

	const { data: ordersData, loading: ordersLoading, refetch: refetchOrders } = useGetOrdersListPaginatedQuery({
		variables: {
			pagination: {
				pageSize: ORDERS_PER_PAGE,
				pageNum: page
			},
			selectionParams: {
				sortParameters: {
					sortOrder: SortOrder.DESC,
					byAttribute: SortAttribute.Id
				},
				filterParameters: {
					bookingStatus: [OrderStatus.Booked, OrderStatus.Confirmed, OrderStatus.Cancelled],
					orderFlownStatus: activeTab.orderFlownStatus
				}
			}
		},
		fetchPolicy: 'no-cache'
	});

	const onTabChange = (value: number) => {
		handleChangePage(1);
		setActiveTab(() => tabs.find(tab => tab.value === value));
	};

	useEffect(() => {
		if (ordersData?.AviaOrdersListPaginated?.pageInfo?.totalPages) {
			const totalPages = ordersData?.AviaOrdersListPaginated?.pageInfo?.totalPages;
			const totalOrdersCount = totalPages ? totalPages * ORDERS_PER_PAGE : null;

			setTotalOrdersCount(totalOrdersCount);
		}
	}, [ordersData?.AviaOrdersListPaginated?.pageInfo?.totalPages]);

	const onOrderClickHandler = (orderId: string) => {
		redirectToOrder(engineURL, orderId);
	};

	const orders = ordersData?.AviaOrdersListPaginated?.edges?.map(edge => edge.node) ?? [];

	const noFlights = !orders.length && !ordersLoading;
	const isActiveFlights = activeTab.orderFlownStatus === OrderFlownStatus.Active;
	const isFlownFlights = activeTab.orderFlownStatus === OrderFlownStatus.Flown;
	const { ProfileControls: ProfileControlsOverride = ProfileControls } = useRenderers();

	return (
		<div className={css.block__gap}>
			{ordersLoading && <SimpleLoader />}

			{
				companyInfo.webskySearchOrder && (
					<Warnings className={css.warning}>
						<span>
							{t('Orders that were booked on a previous version of the site are available')}
							&nbsp;
							<Link className={css.link} action={`${companyInfo.webskySearchOrder}`} target={'_blank'}>
								{t('this link')}
							</Link>
						</span>
					</Warnings>
				)
			}

			<div className={css.orders__header}>
				<h1 className={cn(css.route__title)}>{t('Orders')}</h1>

				<Tabs
					tabs={tabs}
					onChange={onTabChange}
					selected={activeTab.value}
					className={css.tabs}
					flexContainerClassName={css.tabs__flexContainer}
					tabClassName={css.tab}
					tabHeaderClassName={css.tabLabel}
					selectedTabClassName={css.tabSelected}
					indicatorClassName={css.tabIndicator}
					rootTabsClassName={css.tabs__container}
					variant={isTablet ? 'fullWidth' : 'standard'}
					tabsAlign={isTablet ? 'center' : 'left'}
				/>
			</div>

			<div className={css.elements__gap}>
				{noFlights && isActiveFlights && <EmptyPage description={t(`You don't have any upcoming flights`)} />}

				{noFlights && isFlownFlights && <EmptyPage description={t(`You have no flights flown`)} />}

				{orders.map(order => {
					return (
						<OrderCard
							key={order.id}
							order={order as any}
							isCheckinOpen={false}
							type={'account'}
							onClick={onOrderClickHandler}
							onDeleted={refetchOrders}
							hidePrice={
								(order.hideItFares && order.status === OrderStatus.Confirmed) ||
								!order.price?.total?.amount ||
								order.price.total.amount <= 0
							}
						/>
					);
				})}

				<ProfileControlsOverride className={css.profileControls} onRefetchOrders={refetchOrders} />
			</div>

			{!!paginationAvailable && (
				<WxPagination
					className={css.pagination}
					page={page}
					pages={pagesCount}
					onHandleChange={handleChangePage}
				/>
			)}
		</div>
	);
});

export default Orders;
